import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";
import { Link } from "react-router-dom";

const Home = () => {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");
  const [walletData, setWalletData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [ setInsightsData] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  const fetchFinanceSummary = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Wallet
      const walletRes = await fetch(`${BASE_URL}admin/account/wallet`, {
        headers,
      });
      if (walletRes.ok) {
        const walletJson = await walletRes.json();
        if (walletJson.status === "success") setWalletData(walletJson.data);
      }

      // Earnings (last 30 days)
      const endDate = new Date().toISOString().split("T")[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      const earningsRes = await fetch(
        `${BASE_URL}admin/account/earnings/daily?startDate=${startDate}&endDate=${endDate}`,
        { headers },
      );
      if (earningsRes.ok) {
        const earningsJson = await earningsRes.json();
        if (earningsJson.status === "success") setEarningsData(earningsJson.data);
      }

      // Insights
      const insightsRes = await fetch(`${BASE_URL}admin/account/insights`, {
        headers,
      });
      if (insightsRes.ok) {
        const insightsJson = await insightsRes.json();
        if (insightsJson.status === "success") setInsightsData(insightsJson.data);
      }
    } catch (err) {
      console.error("fetchFinanceSummary error:", err);
    }
  };

  const fetchDashboardStats = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}admin/dashboard/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setDashboardData(data.data);
      } else {
        setError("Failed to fetch dashboard data");
      }
    } catch (err) {
      setError("Error fetching dashboard data");
      console.error("Dashboard fetch error:", err);
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      // Clear cache first
      const cacheResponse = await fetch(`${BASE_URL}admin/dashboard/cache`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (cacheResponse.ok) {
        // Refresh dashboard data
        await fetchDashboardStats();
        await fetchFinanceSummary();
        setSuccessMsg("Dashboard refreshed successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError("Failed to refresh cache");
      }
    } catch (err) {
      setError("Error refreshing dashboard");
      console.error("Refresh error:", err);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchFinanceSummary();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content - Responsive layout */}
      <main className="lg:ml-72 min-h-screen main-content">
        {/* Top Header Bar */}
        <div className="bg-white/90 backdrop-blur-md border-b border-stone-200/80 px-4 lg:px-8 py-5 sticky top-0 z-40 shadow-xs">
          <div className="pt-14 lg:pt-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                              <h1 className="text-2xl lg:text-3xl font-black text-stone-950 tracking-tight">
                  Welcome back,{" "}
                  <span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">
                    {admin?.name || "Admin"}
                  </span>
                  !
                </h1>
                <p className="text-sm text-stone-500 font-medium">
                  Comprehensive dashboard overview and analytics.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                {dashboardData?.generatedAt && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#F8D346]/10 border border-[#F8D346]/40 text-xs text-stone-900 font-bold shadow-2xs">
                    <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Updated: {new Date(dashboardData.generatedAt).toLocaleString()}</span>
                    {dashboardData?.cached && (
                      <span className="bg-[#F8D346] text-stone-950 font-black px-1.5 py-0.5 rounded-md text-[10px] border border-[#F5C518]">
                        Cached
                      </span>
                    )}
                  </div>
                )}

                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#F8D346] to-[#F5C518] hover:brightness-95 disabled:opacity-50 text-stone-950 px-4 py-2 rounded-xl text-xs font-black shadow-sm shadow-amber-300/30 border border-[#F5C518] transition-all duration-200 disabled:cursor-not-allowed"
                >
                  <svg
                    className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
                </button>

                <Link
                  to="/admin/services"
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-200 text-stone-700 hover:text-stone-950 hover:border-[#F8D346] hover:bg-[#F8D346]/10 px-3.5 py-2 rounded-xl text-xs font-bold shadow-2xs transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5 text-stone-400 group-hover:text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <span>Services</span>
                </Link>

                <Link
                  to="/admin/finance"
                  className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Finance</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="p-4 lg:p-8">
          {/* Success/Error Messages */}
          {successMsg && (
            <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 font-medium">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                {successMsg}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 font-medium">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-100 border-t-orange-500"></div>
              <span className="ml-4 text-stone-600 font-medium text-lg">
                Loading dashboard...
              </span>
            </div>
          ) : dashboardData ? (
            <>
              {/* Overview Stats - Executive Dashboard Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-8">
                {[
                  {
                    title: "Total Bookings",
                    value: dashboardData.overview.totalBookings?.toLocaleString() || "0",
                    subLabel: "Monthly Volume",
                    subValue: `${dashboardData.overview.monthlyBookings ?? 0} bookings`,
                    iconBg: "bg-orange-500/10 text-orange-600 border border-orange-200/60",
                    accentBorder: "hover:border-orange-400 group-hover:shadow-orange-500/10",
                    badgeBg: "bg-orange-50 text-orange-800 border-orange-200",
                    link: null,
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    ),
                  },
                  {
                    title: "Total Revenue",
                    value: `₹${dashboardData.overview.totalRevenue?.toLocaleString() || "0"}`,
                    subLabel: "This Month",
                    subValue: `₹${dashboardData.overview.monthlyRevenue?.toLocaleString() ?? "0"}`,
                    iconBg: "bg-amber-500/10 text-amber-600 border border-amber-200/60",
                    accentBorder: "hover:border-amber-400 group-hover:shadow-amber-500/10",
                    badgeBg: "bg-amber-50 text-amber-800 border-amber-200",
                    link: "/admin/finance",
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ),
                  },
                  {
                    title: "Total Providers",
                    value: dashboardData.providers.total?.toLocaleString() || "0",
                    subLabel: "Verified Buddies",
                    subValue: `${dashboardData.providers.verified ?? 0} active`,
                    iconBg: "bg-orange-500/10 text-orange-600 border border-orange-200/60",
                    accentBorder: "hover:border-orange-400 group-hover:shadow-orange-500/10",
                    badgeBg: "bg-emerald-50 text-emerald-800 border-emerald-200",
                    link: "/admin/verified-buddies",
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    ),
                  },
                  {
                    title: "Total Users",
                    value: dashboardData.users.total?.toLocaleString() || "0",
                    subLabel: "Verified Accounts",
                    subValue: `${dashboardData.users.verified ?? 0} verified`,
                    iconBg: "bg-amber-500/10 text-amber-700 border border-amber-200/60",
                    accentBorder: "hover:border-amber-400 group-hover:shadow-amber-500/10",
                    badgeBg: "bg-emerald-50 text-emerald-800 border-emerald-200",
                    link: "/admin/users",
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    ),
                  },
                  {
                    title: "Avg Booking Value",
                    value: `₹${dashboardData.overview.averageBookingValue?.toFixed(0) || "0"}`,
                    subLabel: "Average / Ticket",
                    subValue: "Per booking",
                    iconBg: "bg-amber-500/10 text-amber-600 border border-amber-200/60",
                    accentBorder: "hover:border-amber-400 group-hover:shadow-amber-500/10",
                    badgeBg: "bg-stone-100 text-stone-700 border-stone-200",
                    link: null,
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ),
                  },
                  {
                    title: "Completion Rate",
                    value: `${dashboardData.kpis.completionRate || "0"}%`,
                    subLabel: "Overall Health",
                    subValue: "Success rate",
                    iconBg: "bg-emerald-500/10 text-emerald-600 border border-emerald-200/60",
                    accentBorder: "hover:border-emerald-400 group-hover:shadow-emerald-500/10",
                    badgeBg: "bg-emerald-50 text-emerald-800 border-emerald-200",
                    link: null,
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ),
                    subIcon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    ),
                  },
                ].map((stat, idx) => {
                  const CardWrapper = stat.link ? Link : "div";
                  const wrapperProps = stat.link ? { to: stat.link } : {};
                  return (
                    <CardWrapper
                      key={idx}
                      {...wrapperProps}
                      className={`group relative overflow-hidden bg-white rounded-2xl p-6 border border-stone-200/90 shadow-sm ${stat.accentBorder} hover:shadow-xl transition-all duration-300 block`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                          {stat.title}
                        </span>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${stat.iconBg} group-hover:scale-110 transition-transform duration-200 shadow-xs`}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {stat.icon}
                          </svg>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-3xl lg:text-4xl font-black text-stone-900 tracking-tight">
                          {stat.value}
                        </div>
                      </div>

                      <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
                        <span className="text-xs text-stone-400 font-semibold">
                          {stat.subLabel}
                        </span>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${stat.badgeBg}`}>
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {stat.subIcon}
                          </svg>
                          <span>{stat.subValue}</span>
                        </div>
                      </div>
                    </CardWrapper>
                  );
                })}
              </div>

              {/* Provider, Booking, User & KPI Analytics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6 mb-8">
                {/* 1. Provider Stats */}
                <div className="bg-white rounded-2xl p-5 lg:p-6 border border-stone-200/90 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-stone-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-[#F8D346]/25 text-stone-950 border border-[#F8D346] flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-stone-900 tracking-tight">
                            Provider Stats
                          </h3>
                          <p className="text-[11px] font-semibold text-stone-400">Buddies network</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-black px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200">
                        {dashboardData.providers.total ?? 0} Total
                      </span>
                    </div>

                    {/* Metrics List */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Verified</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                          {dashboardData.providers.verified ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Non-Verified</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-amber-50 text-amber-800 border border-amber-200/70">
                          {dashboardData.providers.nonVerified ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Active</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-blue-50 text-blue-700 border border-blue-200/70">
                          {dashboardData.providers.active ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Suspended</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-rose-50 text-rose-700 border border-rose-200/70">
                          {dashboardData.providers.suspended ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar Footer */}
                  <div className="mt-4 pt-3.5 border-t border-stone-100">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-bold text-stone-500">
                        Verification Rate
                      </span>
                      <span className="text-xs font-black text-stone-900">
                        {dashboardData.providers.verificationRate ?? 0}%
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-[#F8D346] to-amber-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(dashboardData.providers.verificationRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* 2. Booking Status */}
                <div className="bg-white rounded-2xl p-5 lg:p-6 border border-stone-200/90 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-stone-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 border border-sky-200/80 flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-stone-900 tracking-tight">
                            Booking Status
                          </h3>
                          <p className="text-[11px] font-semibold text-stone-400">Pipeline distribution</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-black px-2 py-0.5 rounded-md bg-sky-50 text-sky-800 border border-sky-200">
                        Live
                      </span>
                    </div>

                    {/* Metrics List */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Completed</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                          {dashboardData.bookingDistribution.completed ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Accepted</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-blue-50 text-blue-700 border border-blue-200/70">
                          {dashboardData.bookingDistribution.accepted ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Pending</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-amber-50 text-amber-800 border border-amber-200/70">
                          {dashboardData.bookingDistribution.pending ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Cancelled</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-rose-50 text-rose-700 border border-rose-200/70">
                          {dashboardData.bookingDistribution.cancelled ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ratio Distribution Bar Footer */}
                  <div className="mt-4 pt-3.5 border-t border-stone-100">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-bold text-stone-500">
                        Completed vs Cancelled
                      </span>
                      <span className="text-xs font-black text-emerald-700">
                        {dashboardData.kpis.completionRate ?? 0}% comp.
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden flex">
                      <div
                        className="bg-emerald-500 h-full transition-all duration-500"
                        style={{ width: `${Math.min(dashboardData.kpis.completionRate || 0, 100)}%` }}
                        title={`Completed: ${dashboardData.kpis.completionRate}%`}
                      />
                      <div
                        className="bg-rose-400 h-full transition-all duration-500"
                        style={{ width: `${Math.min(dashboardData.kpis.cancellationRate || 0, 100 - (dashboardData.kpis.completionRate || 0))}%` }}
                        title={`Cancelled: ${dashboardData.kpis.cancellationRate}%`}
                      />
                    </div>
                  </div>
                </div>

                {/* 3. User Stats */}
                <div className="bg-white rounded-2xl p-5 lg:p-6 border border-stone-200/90 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-stone-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 border border-orange-200/80 flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-stone-900 tracking-tight">
                            User Stats
                          </h3>
                          <p className="text-[11px] font-semibold text-stone-400">Platform consumers</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-black px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200">
                        {dashboardData.users.total ?? 0} Total
                      </span>
                    </div>

                    {/* Metrics List */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-stone-600 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Total Registered</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-stone-100 text-stone-800 border border-stone-200">
                          {dashboardData.users.total ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Verified</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                          {dashboardData.users.verified ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Suspended</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-rose-50 text-rose-700 border border-rose-200/70">
                          {dashboardData.users.suspended ?? 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#F8D346] shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Active Health</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black bg-[#F8D346]/30 text-stone-950 border border-[#F8D346]">
                          100% Active
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar Footer */}
                  <div className="mt-4 pt-3.5 border-t border-stone-100">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-bold text-stone-500">
                        Verification Rate
                      </span>
                      <span className="text-xs font-black text-emerald-700">
                        {dashboardData.users.verificationRate ?? 0}%
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(dashboardData.users.verificationRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* 4. KPIs & Performance */}
                <div className="bg-white rounded-2xl p-5 lg:p-6 border border-stone-200/90 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-stone-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-stone-950 border border-amber-300 flex items-center justify-center shrink-0 shadow-2xs">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-stone-900 tracking-tight">
                            Key Metrics
                          </h3>
                          <p className="text-[11px] font-semibold text-stone-400">KPI benchmarks</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[11px] font-black px-2 py-0.5 rounded-md bg-[#F8D346]/40 text-stone-950 border border-[#F8D346]">
                        ★ {dashboardData.kpis.averageProviderRating ?? "0"}/5
                      </span>
                    </div>

                    {/* Metrics List */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Completion</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                          {dashboardData.kpis.completionRate ?? 0}%
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Cancellation</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-rose-50 text-rose-700 border border-rose-200/70">
                          {dashboardData.kpis.cancellationRate ?? 0}%
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Avg Rating</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-amber-50 text-amber-800 border border-amber-200/70">
                          {dashboardData.kpis.averageProviderRating ?? "0"}/5
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl hover:bg-stone-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                          <span className="text-xs font-bold text-stone-600">Utilization</span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-black bg-indigo-50 text-indigo-700 border border-indigo-200/70">
                          {dashboardData.kpis.providerUtilizationRate ?? 0}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Utilization Progress Bar Footer */}
                  <div className="mt-4 pt-3.5 border-t border-stone-100">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-bold text-stone-500">
                        Provider Utilization
                      </span>
                      <span className="text-xs font-black text-indigo-700">
                        {dashboardData.kpis.providerUtilizationRate ?? 0}%
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-indigo-400 to-indigo-600 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(dashboardData.kpis.providerUtilizationRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Providers (30%) & Recent Activity (70%) Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-10 gap-6">
                {/* Top Performers (30% width) */}
                <div className="xl:col-span-3 bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col">
                  {/* Card Header */}
                  <div className="p-5 border-b border-stone-100 bg-gradient-to-r from-amber-500/10 via-white to-white flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-[#F8D346] text-stone-950 border border-[#F5C518] flex items-center justify-center shrink-0 shadow-2xs">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 9V4h12v5a6 6 0 01-12 0zm0 0H3a2 2 0 01-2-2V5a2 2 0 012-2h3m12 6h3a2 2 0 002-2V5a2 2 0 00-2-2h-3M9 15v3m6-3v3m-8 3h10"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-sm font-black text-stone-900 tracking-tight">
                          Top Performers
                        </h2>
                        <p className="text-[11px] font-semibold text-stone-400">
                          Best providers by bookings
                        </p>
                      </div>
                    </div>
                    <span className="text-[11px] font-black px-2 py-0.5 rounded-md bg-[#F8D346]/30 text-stone-900 border border-[#F8D346]">
                      Top 5
                    </span>
                  </div>

                  {/* Card Body / Leaderboard */}
                  <div className="p-4 space-y-2.5 flex-1 overflow-y-auto max-h-[640px]">
                    {dashboardData.topProviders.byBookings?.length > 0 ? (
                      dashboardData.topProviders.byBookings.map((provider, index) => {
                        const rankBadgeStyle =
                          index === 0
                            ? "bg-amber-400 text-stone-950 border border-amber-500"
                            : index === 1
                              ? "bg-slate-200 text-slate-900 border border-slate-300"
                              : index === 2
                                ? "bg-amber-100 text-amber-900 border border-amber-300"
                                : "bg-stone-100 text-stone-600 border border-stone-200";

                        return (
                          <Link
                            key={provider.id}
                            to={`/provider/${provider.id}/verified`}
                            state={{ provider: { ...provider, type: "verified" } }}
                            className="group flex items-center justify-between p-3 rounded-xl border border-stone-200/80 hover:border-amber-400 hover:bg-amber-50/20 transition-all duration-200 shadow-2xs hover:shadow-xs"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {/* Avatar with rank indicator */}
                              <div className="relative shrink-0">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-stone-900 to-stone-800 text-[#F8D346] flex items-center justify-center font-black text-sm shadow-2xs group-hover:scale-105 transition-transform">
                                  {provider.name?.charAt(0)?.toUpperCase() || "P"}
                                </div>
                                <span
                                  className={`absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-black shadow-2xs ${rankBadgeStyle}`}
                                >
                                  {index + 1}
                                </span>
                              </div>

                              <div className="min-w-0">
                                <h3 className="text-xs font-black text-stone-900 truncate group-hover:text-amber-700 transition-colors">
                                  {provider.name}
                                </h3>
                                <p className="text-[11px] font-medium text-stone-400 truncate">
                                  {provider.phone}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-amber-500 text-xs">★</span>
                                  <span className="text-[11px] font-black text-stone-800">
                                    {provider.averageRating || "0"}
                                  </span>
                                  <span className="text-[10px] text-stone-400 font-medium truncate">
                                    ({provider.totalReviews || 0} rev)
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="text-right shrink-0 pl-2">
                              <div className="inline-flex flex-col items-end">
                                <span className="text-base font-black text-stone-950 group-hover:text-amber-600 transition-colors">
                                  {provider.completedBookings ?? 0}
                                </span>
                                <span className="text-[9px] font-black uppercase tracking-wider text-stone-400">
                                  Bookings
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-stone-100 flex items-center justify-center text-stone-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <p className="text-xs font-bold text-stone-500">
                          No provider rankings available
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent Activity (70% width) */}
                <div className="xl:col-span-7 bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col">
                  {/* Card Header */}
                  <div className="p-5 border-b border-stone-100 bg-gradient-to-r from-emerald-500/10 via-white to-white flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-stone-900 text-[#F8D346] flex items-center justify-center shrink-0 shadow-2xs">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-sm font-black text-stone-900 tracking-tight">
                          Recent Activity
                        </h2>
                        <p className="text-[11px] font-semibold text-stone-400">
                          Live booking feed and transactional updates
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {dashboardData.recentActivity?.length || 0} Events
                    </span>
                  </div>

                  {/* Activity Feed */}
                  <div className="p-4 space-y-2.5 flex-1 overflow-y-auto max-h-[640px]">
                    {dashboardData.recentActivity?.length > 0 ? (
                      dashboardData.recentActivity.map((activity) => {
                        const isCompleted = activity.status === "COMPLETED";
                        const isCancelled = activity.status === "CANCELLED";
                        const isPending = activity.status === "PENDING";
                        const isAccepted = activity.status === "ACCEPTED";

                        const statusStyle = isCompleted
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                          : isCancelled
                            ? "bg-rose-50 text-rose-700 border-rose-200/80"
                            : isPending
                              ? "bg-amber-50 text-amber-800 border-amber-200/80"
                              : isAccepted
                                ? "bg-blue-50 text-blue-700 border-blue-200/80"
                                : "bg-stone-100 text-stone-700 border-stone-200";

                        const dotColor = isCompleted
                          ? "bg-emerald-500"
                          : isCancelled
                            ? "bg-rose-500"
                            : isPending
                              ? "bg-amber-500"
                              : isAccepted
                                ? "bg-blue-500"
                                : "bg-stone-400";

                        return (
                          <div
                            key={activity.id}
                            className="p-3.5 rounded-xl border border-stone-200/80 hover:border-stone-300 hover:bg-stone-50/50 transition-all duration-200 shadow-2xs"
                          >
                            {/* Row 1: Status, Service, Date & Price */}
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5 pb-2 border-b border-stone-100">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-black tracking-wider uppercase border ${statusStyle}`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                                  {activity.status}
                                </span>

                                <Link
                                  to="/admin/services"
                                  className="text-xs font-black text-stone-900 hover:text-amber-600 transition-colors"
                                >
                                  {activity.service || "General Service"}
                                </Link>
                              </div>

                              <div className="flex items-center gap-2.5">
                                <span className="text-[11px] font-bold text-stone-400">
                                  {new Date(activity.createdAt).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </span>

                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-black ${
                                    activity.finalPrice > 0
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80"
                                      : "bg-stone-100 text-stone-500 border border-stone-200"
                                  }`}
                                >
                                  ₹{activity.finalPrice ?? 0}
                                </span>
                              </div>
                            </div>

                            {/* Row 2: Customer, Provider & Booking Reference */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center text-xs">
                              {/* Customer Chip */}
                              <div className="flex items-center gap-2 p-1.5 rounded-lg bg-stone-50 border border-stone-100 min-w-0">
                                <div className="w-5 h-5 rounded-md bg-stone-200 text-stone-700 flex items-center justify-center text-[10px] font-black shrink-0">
                                  U
                                </div>
                                <div className="min-w-0 truncate">
                                  <span className="text-[10px] font-bold text-stone-400 block leading-none">
                                    Customer
                                  </span>
                                  <span className="font-bold text-stone-800 text-[11px] truncate block leading-tight">
                                    {activity.user || "Unknown"}
                                  </span>
                                  <span className="text-[10px] text-stone-400 block leading-none truncate">
                                    {activity.userPhone || "N/A"}
                                  </span>
                                </div>
                              </div>

                              {/* Provider Chip */}
                              <div className="flex items-center gap-2 p-1.5 rounded-lg bg-stone-50 border border-stone-100 min-w-0">
                                <div className="w-5 h-5 rounded-md bg-[#F8D346]/50 text-stone-900 border border-[#F8D346] flex items-center justify-center text-[10px] font-black shrink-0">
                                  P
                                </div>
                                <div className="min-w-0 truncate">
                                  <span className="text-[10px] font-bold text-stone-400 block leading-none">
                                    Buddy Provider
                                  </span>
                                  <span className="font-bold text-stone-800 text-[11px] truncate block leading-tight">
                                    {activity.provider && activity.provider !== "N/A"
                                      ? activity.provider
                                      : "Not Assigned"}
                                  </span>
                                  <span className="text-[10px] text-stone-400 block leading-none truncate">
                                    {activity.providerPhone && activity.providerPhone !== "N/A"
                                      ? activity.providerPhone
                                      : "—"}
                                  </span>
                                </div>
                              </div>

                              {/* Booking Reference */}
                              <div className="flex items-center justify-between sm:justify-end gap-1.5 sm:px-1">
                                <span
                                  className="text-[10px] font-mono text-stone-400 bg-stone-100/80 px-2 py-1 rounded-md border border-stone-200/60 truncate max-w-full"
                                  title={activity.id}
                                >
                                  ID: {activity.id ? `${activity.id.slice(0, 10)}...` : "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-stone-100 flex items-center justify-center text-stone-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-xs font-bold text-stone-500">
                          No recent transactions recorded
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-gray-500 font-medium text-lg">
                Unable to load dashboard data
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Please try refreshing the page
              </p>
            </div>
          )}
        </div>

        {/* Finance Snapshot - Executive Banking Overview */}
        <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-7 mx-4 lg:mx-8 mb-8 overflow-hidden">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-stone-100">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-[#F8D346] text-stone-950 flex items-center justify-center shrink-0 shadow-xs border border-amber-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base lg:text-lg font-black text-stone-900 tracking-tight">
                    Finance Snapshot
                  </h2>
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                    
                  </span>
                </div>
                <p className="text-xs font-semibold text-stone-400">
                  Real-time wallet balance, commission earnings & transaction velocity
                </p>
              </div>
            </div>

            <Link
              to="/admin/finance"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black bg-stone-900 hover:bg-stone-800 text-white shadow-sm border border-stone-800 hover:border-stone-950 transition-all group shrink-0"
            >
              <span>Open Full Finance</span>
              <svg className="w-3.5 h-3.5 text-[#F8D346] group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>

          {/* 4 Finance Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {/* 1. Current Balance */}
            <div className="p-4 lg:p-5 rounded-2xl bg-gradient-to-b from-stone-900 to-stone-950 text-white border border-stone-800 shadow-sm flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-[#F8D346]/10 rounded-full blur-xl pointer-events-none" />
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Current Balance</span>
                <div className="w-8 h-8 rounded-xl bg-[#F8D346]/20 border border-[#F8D346]/40 text-[#F8D346] flex items-center justify-center text-xs font-black">
                  ₹
                </div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-black text-white tracking-tight">
                  ₹{walletData?.balance?.toLocaleString() || 0}
                </div>
                <div className="mt-2 pt-2 border-t border-stone-800/80 flex items-center justify-between text-[11px]">
                  <span className="text-stone-400 font-medium">Available liquid</span>
                  <span className="text-[#F8D346] font-bold">Admin Wallet</span>
                </div>
              </div>
            </div>

            {/* 2. Lifetime Commission */}
            <div className="p-4 lg:p-5 rounded-2xl bg-white border border-stone-200/90 shadow-2xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Lifetime Commission</span>
                <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-200/70 text-emerald-600 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-black text-stone-900 tracking-tight">
                  ₹{walletData?.lifetimeCommission?.toLocaleString() || 0}
                </div>
                <div className="mt-2 pt-2 border-t border-stone-100 flex items-center justify-between text-[11px]">
                  <span className="text-stone-400 font-medium">Total volume</span>
                  <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                    {walletData?.lifetimeCommissionTransactions || 0} txns
                  </span>
                </div>
              </div>
            </div>

            {/* 3. Last 7 Days */}
            <div className="p-4 lg:p-5 rounded-2xl bg-white border border-stone-200/90 shadow-2xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Last 7 Days</span>
                <div className="w-8 h-8 rounded-xl bg-amber-50 border border-amber-200/70 text-amber-700 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-black text-stone-900 tracking-tight">
                  ₹{walletData?.last7DaysCommission?.toLocaleString() || 0}
                </div>
                <div className="mt-2 pt-2 border-t border-stone-100 flex items-center justify-between text-[11px]">
                  <span className="text-stone-400 font-medium">Recent 7d</span>
                  <span className="font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/60">
                    {walletData?.last7DaysCommissionTransactions || 0} txns
                  </span>
                </div>
              </div>
            </div>

            {/* 4. Avg Transaction */}
            <div className="p-4 lg:p-5 rounded-2xl bg-white border border-stone-200/90 shadow-2xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Avg Transaction</span>
                <div className="w-8 h-8 rounded-xl bg-sky-50 border border-sky-200/70 text-sky-700 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-black text-stone-900 tracking-tight">
                  ₹
                  {walletData && walletData.lifetimeCommissionTransactions > 0
                    ? Math.round(
                        walletData.lifetimeCommission /
                          walletData.lifetimeCommissionTransactions,
                      )
                    : 0}
                </div>
                <div className="mt-2 pt-2 border-t border-stone-100 flex items-center justify-between text-[11px]">
                  <span className="text-stone-400 font-medium">Per ticket</span>
                  <span className="font-bold text-stone-700 bg-stone-100 px-2 py-0.5 rounded-md border border-stone-200">
                    Avg Ticket
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 30-Day Ledger Strip */}
          {earningsData?.totals && (
            <div className="mt-6 pt-5 border-t border-stone-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black uppercase tracking-wider text-stone-400">
                  Monthly Ledger (Last 30 Days)
                </span>
                <span className="text-xs font-bold text-stone-400">
                  {earningsData.totals.count || 0} Total Bookings
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-stone-50/80 border border-stone-200/60">
                  <span className="text-stone-400 font-medium block text-[11px]">Gross Volume</span>
                  <span className="font-black text-stone-900 text-sm mt-0.5 block">
                    ₹{earningsData.totals.gross?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50/60 border border-emerald-200/60">
                  <span className="text-emerald-700 font-medium block text-[11px]">Admin Commission</span>
                  <span className="font-black text-emerald-900 text-sm mt-0.5 block">
                    ₹{earningsData.totals.adminCommission?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-200/60">
                  <span className="text-amber-800 font-medium block text-[11px]">Provider Payouts</span>
                  <span className="font-black text-amber-950 text-sm mt-0.5 block">
                    ₹{earningsData.totals.providerEarnings?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-stone-50/80 border border-stone-200/60">
                  <span className="text-stone-400 font-medium block text-[11px]">Completed Bookings</span>
                  <span className="font-black text-stone-900 text-sm mt-0.5 block">
                    {earningsData.totals.count || 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Home;
